/**
 * Every user-facing text for Platform MCP ("Quadroid"): the user page, the
 * platform admin card, the activity tables and the permission tooltips.
 * Components read from here; don't inline these strings.
 */

export const PLATFORM_MCP_COPY = {
  menu: 'Connect Platform MCP',
  subtitle: 'Use Quadroid from Claude, ChatGPT, Cursor or any MCP app.',
  serverNotReady: "The Quadroid connector isn't available yet. Please ask your admin.",
  noAccess: 'Platform MCP is not enabled for your account. Ask your company admin to give you access.',

  // Connect tab
  serverLink: 'Server link',
  serverLinkTip: 'The same link for everyone. Each user adds their own connection key.',
  createKey: 'Create connection key',
  placeholderHint: 'Create a connection key first; it will be filled in here.',
  filledInHint: (name) =>
    `Filled in with your new key${name ? ` "${name}"` : ''}. It disappears when you leave this page.`,
  howToConnect: 'How to connect your app',

  // Create-key dialog
  keyNameLabel: 'Which app is this key for?',
  keyNamePlaceholder: 'e.g. ChatGPT',
  keyNameTip:
    'The name appears next to every call in your activity, so you can tell your apps apart. One key per app is best.',
  keyNameChips: ['Claude', 'ChatGPT', 'Town', 'Cursor', 'Claude Code', 'Other'],
  keyShownOnce: "Copy it now — you won't see it again.",
  keyCreatedTitle: 'Connection key created',
  keyCreatedSubtitle: 'Your app instructions on the Connect tab are filled in with this key.',
  connectorNotSetUp: "The Quadroid connector isn't set up yet — ask your admin. Your key will work once it is.",
  tooManyKeysLink: 'Go to My keys',

  // Per-app instructions
  linkAppsTip: 'These apps only accept a link, so your key is inside the link. Treat the link like a password.',
  headerAppsTip:
    'The key is sent in a header, not in the link. This is the safer option when your app supports it.',
  claudeTeamNote:
    'Claude Team and Enterprise are not supported yet: there, one connector is shared by the whole organisation.',
  otherAppLabel: 'Any other MCP app',
  otherAppHint: 'Use the server link and send the key in this header:',

  // My keys
  unnamedKey: 'Unnamed',
  never: 'Never',
  lastUsedTip: 'Updated every few minutes, not live.',
  showRevoked: 'Show revoked',
  revokeConfirm: 'Apps connected with this key will stop working right away. Revoke it?',
  keyGone: 'That key no longer exists.',
  noKeys: 'No connection keys yet',
  noKeysHint: 'Create one on the Connect tab, then paste it into your AI app.',

  // What it can do
  readOnly: 'Read only',
  readOnlyTip: 'Only reads your data; changes nothing.',
  makesChanges: 'Makes changes',
  makesChangesTip:
    'Can create, change or run things in your account. The AI app asks you before deleting anything.',
  noTools: 'No tools are available right now.',

  // Activity
  referenceTip: 'Give this code to support if you need help with this call.',
  stoppedTip: 'The AI app stopped waiting before the call finished.',
  activityEmpty: 'No calls in this period.',
  unknown: 'Unknown',
  activityAdminMenu: 'Platform MCP activity',

  // Platform admin card
  setupTip:
    'Creates the Quadroid server the first time. Afterwards it brings its tools up to date and keeps your on/off choices.',
  updateBanner: 'New tools are available after the latest deploy. Click Update.',
  switchOffConfirm:
    'Switch the Quadroid server off for everyone? All connected AI apps stop working until you switch it back on.',
  onOffTip: 'Emergency stop for all users at once. Keys stay valid.',
  rateLimitTip: 'Calls per minute for each connection key. 0 = platform default.',
  platformDefault: 'Platform default',
  deleteToolsTip:
    'Off by default. The AI app always asks the user before deleting, but turn these on only if you want deleting from AI apps.',
  tooManyTools: 'More than 40 tools are on. AI apps choose tools less well with too many.',
  serverCreated: 'Quadroid server created.',
  serverUpdated: 'Quadroid server updated.',
  newTools: (names) => `New tools: ${names.join(', ')}`,
  disabledBySync: (names) => `Turned off because they no longer exist: ${names.join(', ')}`,
  blockingTitle: 'The server was not switched on because of these problems:',

  // Permission editors
  permissionLabel: 'Platform MCP',
  roleTip:
    'Lets users with this role connect AI apps like Claude or ChatGPT to Quadroid with a personal key, and manage and run their agents from there.',
  companyLimitsTip:
    "Lets this company's admin give Platform MCP to chosen users. Users connect AI apps like Claude or ChatGPT to Quadroid with a personal key.",
  partnerLimitsTip:
    'Lets this partner offer Platform MCP to its companies: their users can connect AI apps such as Claude or ChatGPT to Quadroid with a personal key. Off by default; the partner then turns it on per company, and each company admin picks users through a role.',
  untickWarning: 'Users below will lose Platform MCP right away, and their connected AI apps stop working.',
  rolesHint: 'To let people use Platform MCP, create a role with it and assign it to them.',

  // Agent editor
  agentChanged: 'This agent was changed after you opened it. Reload it to see the latest version, then save again.',
};

/**
 * How each `connect.apps` entry is shown, in display order. `kind`:
 *   link   → `steps` text + `url` with a Copy link button (key is in the link)
 *   code   → one command / text block
 *   json   → pretty-printed JSON
 * Unknown keys from the server are appended after these and rendered by type.
 */
export const CONNECT_APPS = [
  { key: 'claude', label: 'Claude (web, desktop, mobile)', kind: 'link' },
  { key: 'chatgpt', label: 'ChatGPT', kind: 'link' },
  { key: 'town', label: 'Town', kind: 'link' },
  { key: 'claude_code', label: 'Claude Code', kind: 'code' },
  { key: 'cursor_or_vscode', label: 'Cursor / VS Code', kind: 'json' },
  { key: 'anthropic_api', label: 'Anthropic API', kind: 'json' },
  { key: 'openai_responses_api', label: 'OpenAI API', kind: 'json' },
  { key: 'curl', label: 'Test with curl', kind: 'code' },
];

/** Activity result filter (`status` query param). */
export const ACTIVITY_STATUS_OPTIONS = [
  { value: 'all', label: 'All results' },
  { value: 'ok', label: 'Success' },
  { value: 'error', label: 'Failed' },
  { value: 'cancelled', label: 'Stopped' },
];

/** Tools that start off and get the delete tooltip on the platform card. */
export const DELETE_TOOLS = ['delete_agent', 'delete_folder', 'delete_file'];

export const TOO_MANY_TOOLS = 40;
